import { APIClientServices } from "./APIClientServices";
import { PublicMcpDTO, SeniorityLevel } from "./McpLibraryServices";
import { PublicMemberLibraryDTO } from "./MemberLibraryServices";

// --- DTOs ---

interface WorkspaceContractedMcpDTO {
	id: string;
	mcpId: string;
	teamMemberId: string;
	userId: string;
	config: Record<string, any>;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	mcp?: PublicMcpDTO;
}

export interface HireMemberPayload {
	member_library_id: string;
	mcp_id?: string;
	name?: string;
	seniority_level?: SeniorityLevel;
	seniority?: SeniorityLevel;
	custom_system_prompt?: string | null;
	config?: Record<string, any>;
	mcp_settings?: TeamMemberMcpSettingDTO[];
}

export interface UpdateMemberPayload {
	name?: string;
	member_library_id?: string | null;
	seniority_level?: SeniorityLevel;
	seniority?: SeniorityLevel;
	custom_system_prompt?: string | null;
	system_prompt?: string | null;
	config?: Record<string, any>;
	mcp_id?: string;
	mcp_settings?: TeamMemberMcpSettingDTO[];
	is_active?: boolean;
}

export interface TeamMemberMcpSettingDTO {
	mcp_id: string;
	is_active?: boolean;
	config?: Record<string, any>;
}

export interface PublicTeamMemberMcpDTO {
	id: string;
	teamMemberId: string;
	mcpId: string;
	contractedMcpId: string | null;
	configOverride: Record<string, any>;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	mcp?: PublicMcpDTO;
	contractedMcp?: WorkspaceContractedMcpDTO;
}

export interface PublicTeamLeaderDTO {
	id: number;
	workspaceId: number;
	teamId: number;
	name: string;
	systemPrompt: string;
	personalityConfig: Record<string, any>;
	allowedMcps: string[];
	modelProvider: string;
	modelName: string;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface PublicWorkspaceDTO {
	id: number;
	ownerId: string;
	name: string;
	description: string | null;
	settings: Record<string, any>;
	brandContext: Record<string, any> | null;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	membersCount?: number;
	teamsCount?: number;
	leader?: PublicTeamLeaderDTO;
}

export interface CreateWorkspacePayload {
	name: string;
	description?: string;
	settings?: Record<string, any>;
}

export interface CreateWorkspaceResult {
	workspace: PublicWorkspaceDTO;
	leader?: PublicTeamLeaderDTO | null;
}

export interface PublicTeamDTO {
	id: number;
	workspaceId: number;
	name: string;
	description: string | null;
	isActive: boolean;
	members?: string[] | null; // Lista de IDs dos membros ativos do team
	leader?: string | null; // ID do líder atual do team
	createdAt: string;
	updatedAt: string;
}

export interface PublicTeamMemberDTO {
	id: string;
	teamId: string;
	workspaceId: string | null;
	memberLibraryId: string | null;
	name: string;
	seniorityLevel: SeniorityLevel;
	capacityUsed: number;
	customSystemPrompt: string | null;
	isActive: boolean;
	memberLibrary?: PublicMemberLibraryDTO;
	mcps: PublicTeamMemberMcpDTO[];
	activeMcps: PublicTeamMemberMcpDTO[];
	/** IDs dos MCPs permitidos pelo template (member_library_mcps). UI filtra picker por essa lista. */
	libraryMcpIds?: string[];
	rateLimitPerMinute?: number;
	tokensUsed: number;
	tokensBonus: number;
	tokenLimit: number | null;
	tokenLimitReached: boolean;
	/** EK-406: próximo reset automático (30 dias após contratação ou último reset). */
	tokensResetAt: string | null;
	/** EK-406: timestamp do último reset efetivo. */
	lastResetAt: string | null;
	/** EK-455: instruções permanentes do usuário lidas pelo agente em cada execução. */
	observations: string | null;
	/** EK-455: pausado — não recebe novas tarefas; tarefas em andamento continuam. */
	isPaused: boolean;
	createdAt: string;
	updatedAt: string;

	// Compatibilidade legada
	seniority: SeniorityLevel;
	systemPrompt?: string | null;
	contractedMcp?: WorkspaceContractedMcpDTO;
}

// --- Service ---

export class WorkspaceServices {
	private static readonly BASE_ROUTE = "v1/workspaces";

	// ── Workspaces ────────────────────────────────────────────────

	/** Lista todos os workspaces do usuário autenticado. */
	static async listWorkspaces(): Promise<PublicWorkspaceDTO[]> {
		return APIClientServices.get<PublicWorkspaceDTO[]>(this.BASE_ROUTE);
	}

	/** Obtém detalhes de um workspace pelo ID. */
	static async getWorkspace(id: string): Promise<PublicWorkspaceDTO> {
		return APIClientServices.get<PublicWorkspaceDTO>(`${this.BASE_ROUTE}/${id}`);
	}

	/** Cria um novo workspace para o usuário autenticado. */
	static async createWorkspace(data: CreateWorkspacePayload): Promise<CreateWorkspaceResult> {
		return APIClientServices.post<CreateWorkspaceResult>(this.BASE_ROUTE, data);
	}

	// ── Teams ─────────────────────────────────────────────────────

	/** Lista teams de um workspace. */
	static async listTeams(workspaceId: string): Promise<PublicTeamDTO[]> {
		return APIClientServices.get<PublicTeamDTO[]>(`${this.BASE_ROUTE}/${workspaceId}/teams`);
	}

	/** Obtém detalhes de um team. */
	static async getTeam(workspaceId: string, teamId: string): Promise<PublicTeamDTO> {
		return APIClientServices.get<PublicTeamDTO>(`${this.BASE_ROUTE}/${workspaceId}/teams/${teamId}`);
	}

	/** Cria um novo team no workspace. */
	static async createTeam(workspaceId: string, data: { name: string; description?: string }): Promise<PublicTeamDTO> {
		return APIClientServices.post<PublicTeamDTO>(`${this.BASE_ROUTE}/${workspaceId}/teams`, data);
	}

	/** Atualiza um team. */
	static async updateTeam(workspaceId: string, teamId: string, data: { name?: string; description?: string }): Promise<PublicTeamDTO> {
		return APIClientServices.put<PublicTeamDTO>(`${this.BASE_ROUTE}/${workspaceId}/teams/${teamId}`, data);
	}

	/** Remove um team. */
	static async deleteTeam(workspaceId: string, teamId: string): Promise<void> {
		await APIClientServices.delete(`${this.BASE_ROUTE}/${workspaceId}/teams/${teamId}`);
	}

	// ── Membros (por team) ────────────────────────────────────────

	/** Lista membros de um team. */
	static async getMembers(workspaceId: string, teamId: string): Promise<PublicTeamMemberDTO[]> {
		return APIClientServices.get<PublicTeamMemberDTO[]>(`${this.BASE_ROUTE}/${workspaceId}/teams/${teamId}/members`);
	}

	/** Obtém detalhes de um membro específico. */
	static async getMember(workspaceId: string, teamId: string, memberId: string): Promise<PublicTeamMemberDTO> {
		return APIClientServices.get<PublicTeamMemberDTO>(`${this.BASE_ROUTE}/${workspaceId}/teams/${teamId}/members/${memberId}`);
	}

	/** Contrata um novo membro no team. */
	static async hireMember(workspaceId: string, teamId: string, data: HireMemberPayload): Promise<PublicTeamMemberDTO> {
		return APIClientServices.post<PublicTeamMemberDTO>(`${this.BASE_ROUTE}/${workspaceId}/teams/${teamId}/members`, data);
	}

	/** Atualiza configurações de um membro. */
	static async updateMember(workspaceId: string, teamId: string, memberId: string, data: UpdateMemberPayload): Promise<PublicTeamMemberDTO> {
		return APIClientServices.put<PublicTeamMemberDTO>(`${this.BASE_ROUTE}/${workspaceId}/teams/${teamId}/members/${memberId}`, data);
	}

	/** Remove (desliga) um membro do team. */
	static async removeMember(workspaceId: string, teamId: string, memberId: string): Promise<void> {
		await APIClientServices.delete(`${this.BASE_ROUTE}/${workspaceId}/teams/${teamId}/members/${memberId}`);
	}

	/** Adiciona tokens de bônus manualmente a um membro específico (`reason` opcional para auditoria). */
	static async addMemberTokens(
		workspaceId: string,
		teamId: string,
		memberId: string,
		tokens: number,
		reason?: string,
	): Promise<PublicTeamMemberDTO> {
		return APIClientServices.post<PublicTeamMemberDTO>(
			`${this.BASE_ROUTE}/${workspaceId}/teams/${teamId}/members/${memberId}/tokens`,
			{ tokens, ...(reason ? { reason } : {}) },
		);
	}

	/**
	 * EK-408: Faz upload de um arquivo para storage persistente, retorna o
	 * registro com `id` que pode ser usado em `attachments: [{attachmentId}]`
	 * no envio de mensagem. Substitui o fluxo antigo de mandar base64 inline.
	 */
	static async uploadChatAttachment(workspaceId: string, file: File): Promise<{
		id: string;
		filename: string;
		mimeType: string;
		sizeBytes: number;
		storageUrl: string;
		createdAt: string;
	}> {
		const formData = await APIClientServices.formData({ file });
		return APIClientServices.post(`${this.BASE_ROUTE}/${workspaceId}/uploads/chat-attachment`, formData);
	}
}
